import { useMemo, useState } from 'react';
import axios from 'axios';
import {
  X,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Loader2,
  Zap,
  Clock,
  Info,
} from 'lucide-react';
import { API } from '../App';
import { invalidateAccountBalances, beginSync, endSync } from '../hooks/useAccountBalances';

/**
 * Rebalance experience — clear, confident one-click correction workflow.
 *
 * - Leads with actual transfer actions in plain English, not diagnostics.
 * - Shows Before → After per account so the user knows what will change.
 * - Apply persists after_balances to user_actual_balances via a dedicated
 *   POST /api/settings/apply-rebalance endpoint, then invalidates the shared
 *   balance cache so Dashboard / Accounts / SystemHealthPill refresh.
 * - Uses only canonical expected-vs-actual numbers from the rebalance
 *   endpoint (already year-scoped via /api/settings/rebalance-suggestions).
 */

// ------- Account visual system -----------------------------------------------
const ACCOUNT_META = {
  personal: { label: 'Personal', tone: 'emerald', hex: 'bg-emerald-500' },
  business: { label: 'Business', tone: 'blue',    hex: 'bg-blue-500' },
  taxes:    { label: 'Taxes',    tone: 'rose',    hex: 'bg-rose-500' },
  savings:  { label: 'Savings',  tone: 'amber',   hex: 'bg-amber-500' },
};

const fmtMoney = (n) => {
  const v = Number(n || 0);
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

const fmtMoneyExact = (n) => {
  const v = Number(n || 0);
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

function AccountDot({ name }) {
  const meta = ACCOUNT_META[name] || { hex: 'bg-gray-400' };
  return <span className={`inline-block w-2.5 h-2.5 rounded-full ${meta.hex}`} aria-hidden="true" />;
}

function AccountChip({ name, testid }) {
  const meta = ACCOUNT_META[name] || { label: name, hex: 'bg-gray-400' };
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-800 dark:text-gray-100"
    >
      <span className={`inline-block w-2 h-2 rounded-full ${meta.hex}`} aria-hidden="true" />
      {meta.label}
    </span>
  );
}

// ------- Outcome summary (3 stats) ------------------------------------------
function OutcomeSummary({ overCount, underCount, totalToMove }) {
  return (
    <div
      data-testid="rebalance-outcome-summary"
      className="grid grid-cols-3 gap-2 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/60 p-3"
    >
      <div className="text-center">
        <div className="text-xs text-gray-500 dark:text-gray-400">Above expected</div>
        <div data-testid="rebalance-stat-over" className="text-lg font-semibold text-amber-600 dark:text-amber-400">{overCount}</div>
      </div>
      <div className="text-center border-x border-gray-200 dark:border-gray-700">
        <div className="text-xs text-gray-500 dark:text-gray-400">Below expected</div>
        <div data-testid="rebalance-stat-under" className="text-lg font-semibold text-rose-600 dark:text-rose-400">{underCount}</div>
      </div>
      <div className="text-center">
        <div className="text-xs text-gray-500 dark:text-gray-400">To move</div>
        <div data-testid="rebalance-stat-total" className="text-lg font-semibold text-blue-600 dark:text-blue-400">{fmtMoney(totalToMove)}</div>
      </div>
    </div>
  );
}

// ------- Transfer row --------------------------------------------------------
function TransferRow({ transfer, idx }) {
  return (
    <div
      data-testid={`rebalance-transfer-${idx}`}
      className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700"
    >
      <div className="flex items-center gap-2 text-sm">
        <span className="font-semibold text-gray-900 dark:text-gray-100">
          Move {fmtMoney(transfer.amount)}
        </span>
        <span className="text-gray-500 dark:text-gray-400">from</span>
        <AccountChip name={transfer.from} testid={`transfer-${idx}-from`} />
        <ArrowRight className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" />
        <span className="text-gray-500 dark:text-gray-400">to</span>
        <AccountChip name={transfer.to} testid={`transfer-${idx}-to`} />
      </div>
    </div>
  );
}

// ------- Before / After table -----------------------------------------------
function BeforeAfterTable({ accountHealth, afterBalances }) {
  const order = ['personal', 'business', 'taxes', 'savings'];
  return (
    <div
      data-testid="rebalance-before-after"
      className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
    >
      <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 px-3 py-2 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60">
        <span>Account</span>
        <span className="text-right">Before</span>
        <span className="text-right">After</span>
        <span className="text-right">Target</span>
      </div>
      {order.map((acc) => {
        const h = accountHealth?.[acc];
        if (!h) return null;
        const after = afterBalances?.[acc] ?? h.actual;
        return (
          <div
            key={acc}
            data-testid={`rebalance-before-after-${acc}`}
            className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 px-3 py-2 items-center border-t border-gray-200 dark:border-gray-700 text-sm"
          >
            <span className="flex items-center gap-2 font-medium text-gray-800 dark:text-gray-200">
              <AccountDot name={acc} />
              {ACCOUNT_META[acc]?.label || acc}
            </span>
            <span className="text-right font-mono tabular-nums text-gray-600 dark:text-gray-300">
              {fmtMoneyExact(h.actual)}
            </span>
            <span className="text-right font-mono tabular-nums font-semibold text-gray-900 dark:text-gray-100">
              {fmtMoneyExact(after)}
            </span>
            <span className="text-right font-mono tabular-nums text-gray-500 dark:text-gray-400">
              {fmtMoneyExact(h.target)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ------- Main modal ----------------------------------------------------------
export default function RebalanceModal({
  open,
  onClose,
  rebalanceData,
  selectedYear,
  onApplied,
  onReloadPlan,
  catchupWeeks,
  onCatchupWeeksChange,
}) {
  const [mode, setMode] = useState('instant'); // 'instant' | 'catchup'
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [appliedSummary, setAppliedSummary] = useState(null); // { transfers_count, message, before, after }

  const data = rebalanceData || {};
  const accountHealth = data.account_health || {};
  const transfers = data.instant_transfers || [];
  const afterBalances = data.after_balances || {};

  const { overCount, underCount, totalToMove, aligned } = useMemo(() => {
    const values = Object.values(accountHealth);
    const over = values.filter((h) => h.status === 'overfunded').length;
    const under = values.filter((h) => h.status === 'underfunded').length;
    const total = transfers.reduce((s, t) => s + Number(t.amount || 0), 0);
    const isAligned = over === 0 && under === 0 && transfers.length === 0;
    return { overCount: over, underCount: under, totalToMove: total, aligned: isAligned };
  }, [accountHealth, transfers]);

  const weeklyPlan = data.weekly_plan || {};
  const weeklyAccounts = weeklyPlan.accounts || {};

  const handleApply = async () => {
    setApplyError(null);
    setApplying(true);
    // Snapshot the pre-apply amount so the success screen reports what was actually moved.
    const preApplyTotal = totalToMove;
    const preApplyTransferCount = transfers.length;
    beginSync?.('Applying rebalance…');
    try {
      const qs = selectedYear ? `?year=${selectedYear}` : '';
      const res = await axios.post(`${API}/settings/apply-rebalance${qs}`);
      const data = res.data || {};
      setAppliedSummary({
        ...data,
        transfers_count: data.transfers_count ?? preApplyTransferCount,
        moved_total: preApplyTotal,
      });
      // Invalidate shared balance cache so Dashboard / Accounts / SystemHealthPill
      // all refresh immediately.
      invalidateAccountBalances();
      onApplied?.();
      endSync?.({ success: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Rebalance couldn't be applied. No changes were saved.";
      setApplyError(msg);
      endSync?.({ success: false });
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    // Reset transient state for next open
    setAppliedSummary(null);
    setApplyError(null);
    setMode('instant');
    onClose?.();
  };

  if (!open) return null;

  // ========== SUCCESS STATE ==========
  if (appliedSummary) {
    const n = appliedSummary.transfers_count || 0;
    const isNoop = appliedSummary.no_op || n === 0;
    return (
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        role="dialog"
        aria-modal="true"
        data-testid="rebalance-modal-success"
      >
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
          <div className="p-6 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mb-4">
              <Sparkles className="w-7 h-7 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
            </div>
            <h2
              data-testid="rebalance-success-title"
              className="text-xl font-bold text-gray-900 dark:text-white"
            >
              Rebalance Applied
            </h2>
            <p
              data-testid="rebalance-success-body"
              className="mt-2 text-sm text-gray-600 dark:text-gray-300"
            >
              {isNoop
                ? 'Your accounts are already aligned. Nothing needed to change.'
                : 'Your balances have been updated and your system is now closer to alignment.'}
            </p>
            {!isNoop && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {n} transfer{n === 1 ? '' : 's'} recorded • {fmtMoney(appliedSummary.moved_total ?? 0)} moved
              </p>
            )}
          </div>
          <div className="flex gap-2 p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <button
              type="button"
              onClick={handleClose}
              data-testid="rebalance-success-close"
              className="flex-1 px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700 transition-colors"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => {
                handleClose();
                window.location.href = '/settings?tab=balances';
              }}
              data-testid="rebalance-success-view-balances"
              className="flex-1 px-4 py-2 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            >
              View My Balances
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ========== PLAN REVIEW STATE ==========
  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="rebalance-modal"
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex items-start justify-between gap-4">
          <div>
            <h2
              data-testid="rebalance-modal-title"
              className="text-lg font-bold text-gray-900 dark:text-white"
            >
              Rebalance Plan
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
              These transfers will move your balances toward their expected targets.
            </p>
            <p
              data-testid="rebalance-modal-explainer"
              className="mt-2 flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400"
            >
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span>
                Rebalancing compares your actual balances to their expected balances based on income allocations and expenses.
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            data-testid="rebalance-modal-close"
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1 rounded-md"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mode toggle */}
        <div className="px-5 pt-4">
          <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-0.5 bg-gray-100 dark:bg-gray-800" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'instant'}
              data-testid="rebalance-mode-instant"
              onClick={() => setMode('instant')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                mode === 'instant'
                  ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Zap className="w-3.5 h-3.5" aria-hidden="true" />
              Instant
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'catchup'}
              data-testid="rebalance-mode-catchup"
              onClick={() => setMode('catchup')}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                mode === 'catchup'
                  ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <Clock className="w-3.5 h-3.5" aria-hidden="true" />
              Catch-Up
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Aligned short-circuit */}
          {aligned && mode === 'instant' && (
            <div
              data-testid="rebalance-aligned-state"
              className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-center"
            >
              <CheckCircle2 className="w-10 h-10 text-emerald-600 dark:text-emerald-400 mx-auto mb-2" aria-hidden="true" />
              <p className="font-semibold text-emerald-700 dark:text-emerald-300">
                Your accounts are aligned
              </p>
              <p className="mt-1 text-sm text-emerald-700/80 dark:text-emerald-300/80">
                No rebalance needed right now.
              </p>
            </div>
          )}

          {/* INSTANT MODE */}
          {mode === 'instant' && !aligned && (
            <>
              <OutcomeSummary
                overCount={overCount}
                underCount={underCount}
                totalToMove={totalToMove}
              />

              {/* Transfer list */}
              <div>
                <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                  Transfers to make
                </h3>
                {transfers.length === 0 ? (
                  <p
                    data-testid="rebalance-no-transfers"
                    className="text-sm text-gray-500 dark:text-gray-400 italic"
                  >
                    No transfers needed — your accounts are within tolerance.
                  </p>
                ) : (
                  <div data-testid="rebalance-transfer-list" className="space-y-2">
                    {transfers.map((t, idx) => (
                      <TransferRow key={`${t.from}-${t.to}-${idx}`} transfer={t} idx={idx} />
                    ))}
                  </div>
                )}
              </div>

              {/* Before / After */}
              {transfers.length > 0 && (
                <BeforeAfterTable
                  accountHealth={accountHealth}
                  afterBalances={afterBalances}
                />
              )}

              {/* Minimum balance notice (kept — important signal) */}
              {(data.minimum_balance_warnings || []).length > 0 && (
                <div
                  data-testid="rebalance-min-warnings"
                  className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3"
                >
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">
                    Minimum balance protection
                  </p>
                  <ul className="space-y-0.5">
                    {data.minimum_balance_warnings.map((w) => (
                      <li key={`min-${w.account || w.message}`} className="text-xs text-amber-700/90 dark:text-amber-300/90">
                        {w.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* CATCH-UP MODE */}
          {mode === 'catchup' && (
            <div data-testid="rebalance-catchup-panel" className="space-y-4">
              <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-3 text-xs text-blue-700 dark:text-blue-300">
                Catch-Up is a weekly suggestion — it spreads the gap across {catchupWeeks} weeks instead of a single transfer.
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                <span>Spread over:</span>
                <div className="flex gap-1">
                  {[2, 4, 6, 8].map((w) => (
                    <button
                      key={w}
                      type="button"
                      onClick={() => onCatchupWeeksChange?.(w)}
                      data-testid={`rebalance-catchup-weeks-${w}`}
                      className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${
                        catchupWeeks === w
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {w} wks
                    </button>
                  ))}
                </div>
              </div>

              {Object.keys(weeklyAccounts).length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  No catch-up needed.
                </p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(weeklyAccounts).map(([acc, plan]) => (
                    <div
                      key={acc}
                      data-testid={`rebalance-catchup-${acc}`}
                      className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900"
                    >
                      <div className="flex items-center gap-2">
                        <AccountDot name={acc} />
                        <div>
                          <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
                            {ACCOUNT_META[acc]?.label || acc}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Short by {fmtMoneyExact(plan.total_deficit)}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-base font-semibold text-gray-900 dark:text-white">
                          {fmtMoneyExact(plan.weekly_amount)}
                        </div>
                        <div className="text-[10px] text-gray-500 dark:text-gray-400">per week</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {data.income_warning && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
                  {data.income_warning}
                </div>
              )}
            </div>
          )}

          {/* Error alert */}
          {applyError && (
            <div
              data-testid="rebalance-apply-error"
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-700 dark:text-rose-300"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span>{applyError}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 space-y-3">
          <p
            data-testid="rebalance-confidence-helper"
            className="text-[11px] leading-relaxed text-gray-500 dark:text-gray-400"
          >
            Rebalance does not change your income or expenses. It only helps bring your account
            balances back toward their expected targets.
          </p>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <button
              type="button"
              onClick={handleClose}
              data-testid="rebalance-cancel"
              className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700 transition-colors"
              disabled={applying}
            >
              Cancel
            </button>
            {mode === 'instant' && (
              <button
                type="button"
                onClick={handleApply}
                data-testid="rebalance-apply"
                disabled={applying || (aligned && transfers.length === 0)}
                className="inline-flex items-center justify-center gap-2 px-5 py-2 text-sm font-semibold rounded-md bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white shadow-sm transition-colors"
              >
                {applying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    Applying…
                  </>
                ) : (
                  <>
                    Apply Rebalance
                    <ArrowRight className="w-4 h-4" aria-hidden="true" />
                  </>
                )}
              </button>
            )}
            {mode === 'catchup' && (
              <button
                type="button"
                onClick={() => setMode('instant')}
                data-testid="rebalance-switch-to-instant"
                className="inline-flex items-center justify-center gap-2 px-5 py-2 text-sm font-semibold rounded-md bg-blue-600 hover:bg-blue-700 text-white shadow-sm transition-colors"
              >
                Switch to Instant to Apply
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
